import type { DamageZone } from "../types"
import type { CarArea } from "./areas"
import { VIN_COPY } from "./vin/use-vin-step"
import type { Corner, PhoneCheck, PlannedShot, ShotVerdict } from "./types"

// Every word the consumer reads (or hears) in the photo walk. Plain US
// English, second person, short imperatives; the system blames itself, never
// the person. The VIN step says where the sticker is before it says "VIN".
// No "AI" in coaching.

// Panel names as a car owner says them (driver = left).
export const ZONE_NAME: Record<DamageZone, string> = {
  "front-bumper": "Front bumper",
  hood: "Hood",
  windshield: "Windshield",
  roof: "Roof",
  "front-fender-left": "Driver's front fender",
  "front-fender-right": "Passenger front fender",
  "front-door-left": "Driver's front door",
  "front-door-right": "Passenger front door",
  "rear-door-left": "Driver's back door",
  "rear-door-right": "Passenger back door",
  "quarter-panel-left": "Driver's rear fender",
  "quarter-panel-right": "Passenger rear fender",
  trunk: "Trunk",
  "rear-bumper": "Back bumper",
}

export const CORNER_NAME: Record<Corner, string> = {
  FL: "Front corner, driver's side",
  FR: "Front corner, passenger side",
  RR: "Back corner, passenger side",
  RL: "Back corner, driver's side",
}

const CORNER_WALK: Record<Corner, string> = {
  FL: "the front corner on the driver's side",
  FR: "the front corner on the passenger side",
  RR: "the back corner on the passenger side",
  RL: "the back corner on the driver's side",
}

export function shotTitle(shot: PlannedShot): string {
  switch (shot.kind) {
    case "wide":
      return shot.corner ? CORNER_NAME[shot.corner] : "Whole car"
    case "closer":
      return "Closer to the damage"
    case "closeup":
      return shot.zone ? `${ZONE_NAME[shot.zone]}, up close` : "The damage, up close"
    case "sticker":
      return VIN_COPY.title
  }
}

// One line under the title: what to do, in the order you do it.
export function shotInstruction(shot: PlannedShot): string {
  switch (shot.kind) {
    case "wide":
      return "Step back until the whole car fits in the picture."
    case "closer":
      return "Take a few steps toward the damage. Fit the damaged panels in the frame."
    case "closeup":
      return shot.zone
        ? "Get about an arm's length away. Fill the box with the damage."
        : "Get about an arm's length from the worst of it. Fill the box with the damage."
    case "sticker":
      return VIN_COPY.instruction
  }
}

// Shown on the walk card between shots.
export function walkLine(shot: PlannedShot): string {
  switch (shot.kind) {
    case "wide":
      return shot.corner ? `Walk to ${CORNER_WALK[shot.corner]}` : "Step back from the car"
    case "closer":
      return "Now walk toward the damage"
    case "closeup":
      return shot.zone ? `Get close to the ${ZONE_NAME[shot.zone].toLowerCase()}` : "Get close to the damage"
    case "sticker":
      return VIN_COPY.walk
  }
}

// What the screen reader hears when a shot opens.
export function shotAnnouncement(shot: PlannedShot, index: number, total: number): string {
  return `Photo ${index + 1} of ${total}: ${shotTitle(shot)}. ${shotInstruction(shot)}`
}

export const VERDICT_LINE: Record<Exclude<ShotVerdict, "wrong-side">, string> = {
  good: "Got it",
  dark: "That one's a bit dark. Find more light or turn a light on.",
  blurry: "That one's a bit blurry. Hold still and try again.",
  "too-close": "Step back so the whole car fits.",
  "too-far": "That's the whole car. Come closer to the damage.",
  "low-quality": "Hard to make out. Let's try that one again.",
}

export function wrongSideQuestion(shot: PlannedShot): string {
  return `Is this the ${shotTitle(shot).toLowerCase()}?`
}

// Short reason chips on Review tiles.
export const VERDICT_CHIP: Partial<Record<ShotVerdict, string>> = {
  dark: "A bit dark",
  blurry: "A bit blurry",
  "too-close": "Car cut off",
  "too-far": "Too far away",
  "low-quality": "Hard to see",
  "wrong-side": "Different side?",
}

const END_NAME = { front: "front", rear: "back" } as const
const SIDE_NAME = { left: "driver's", right: "passenger" } as const

// The phone's verdict, said back in words.
export function phoneLine(shot: PlannedShot, phone: PhoneCheck): string {
  if (phone.verdict === "ok") {
    if (shot.kind === "wide" && shot.corner) return `That's ${CORNER_WALK[shot.corner]}.`
    return "Close enough to see the damage."
  }
  if (phone.verdict === "same-spot" && shot.corner) {
    return `Looks like the same spot as the last photo. Walk to ${CORNER_WALK[shot.corner]}.`
  }
  if (phone.verdict === "wrong-corner" && shot.corner && phone.seen) {
    const askedEnd = shot.corner === "FL" || shot.corner === "FR" ? "front" : "rear"
    const askedSide = shot.corner === "FL" || shot.corner === "RL" ? "left" : "right"
    if (phone.seen.end && phone.seen.end !== askedEnd) {
      return `This looks like the ${END_NAME[phone.seen.end]} of the car. Walk to the ${END_NAME[askedEnd]}.`
    }
    if (phone.seen.side && phone.seen.side !== askedSide) {
      return `This looks like the ${SIDE_NAME[phone.seen.side]} side. Walk round to the ${SIDE_NAME[askedSide]} side.`
    }
  }
  return wrongSideQuestion(shot)
}

export const CAPTURE_COPY = {
  zones: {
    title: "Where's the damage?",
    sub: "Tap the side of the car that's hit. More than one is fine.",
    area: { front: "Front", back: "Back", driver: "Driver's side", passenger: "Passenger side", roof: "Roof" } as Record<CarArea, string>,
    // What a screen reader announces (never just "Back": that's the frame's button).
    areaName: { front: "Front of the car", back: "Back of the car", driver: "Driver's side", passenger: "Passenger side", roof: "Roof" } as Record<CarArea, string>,
    partsTitle: { front: "Which part of the front?", back: "Which part of the back?", driver: "Which part of the driver's side?", passenger: "Which part of the passenger side?", roof: "" } as Record<CarArea, string>,
    unsure: "Not sure",
    unsureSub: "We'll photograph the whole car",
    nudgeNothing: "Tap the side that's hit, or Not sure.",
    nudgePart: (area: string) => `Pick which part of the ${area.toLowerCase()}.`,
    next: "Next",
  },
  brief: {
    title: (n: number) => `${n} photos, about ${Math.max(1, Math.round((n * 18) / 60))} minute${Math.round((n * 18) / 60) > 1 ? "s" : ""}`,
    sub: "We'll walk you around the car one photo at a time. Start where the damage is.",
    startAt: (corner: Corner) => `First stop: ${CORNER_WALK[corner]}.`,
    // The VIN step opens the walk when the shop wants the car and has no VIN.
    startAtVin: (corner: Corner) => `Start at the driver's door for the VIN, then ${CORNER_WALK[corner]}.`,
    tips: [
      "Good light helps: daylight or a bright garage.",
      "Hold your phone upright. Each step shows you exactly what to photograph.",
    ],
    tipLive: "Hold your phone upright. We'll tell you when to step back or come closer.",
    inApp: {
      title: "For the smart camera, open this in your browser",
      body: "This app's built-in browser can't run it. Copy the link and paste it into Safari or Chrome, or just use your phone's camera here.",
      copy: "Copy link",
      copied: "Link copied",
    },
    open: "Open the camera",
    permission: "Your phone will ask to use the camera. Tap Allow.",
    gallery: "Already took photos? Choose from your gallery",
    later: "Not at the car? Text me a link for later",
    laterSent: (sentTo: string) => `Link texted to ${sentTo}`,
  },
  coach: {
    open: "Take the photo",
    likeThis: "Like this",
    skip: "Can't get this one",
    checking: "Checking your photo…",
    keep: "Use it anyway",
    retake: "Retake",
    next: "Next",
    yesRight: "Yes, that's right",
    review: "See all photos",
    offline: (n: number) => `Offline · ${n} waiting to send`,
    failed: "Couldn't send that one",
    retry: "Try again",
    skipForNow: "Skip for now",
    notImage: "That file isn't a photo we can use. Try another one.",
    unreadable: "Couldn't open that photo. Try taking it again.",
    tooLarge: "That photo is too big. Try another one.",
  },
  review: {
    title: "Your photos",
    sub: "Tap any photo to retake it.",
    skipped: "Skipped · tap to take",
    missing: "Tap to take",
    uploading: "Sending…",
    queued: "Waiting for signal",
    failed: "Not sent · tap to retry",
    checked: "Checked",
    retakeSuggested: "Retake suggested",
    addSpot: "Add another damaged spot",
    addFromGallery: "Add photos from your gallery",
    move: "Move",
    moveTitle: "Which photo is this?",
    moveToExtras: "Just an extra photo",
    takeMissing: (n: number) => `Take the ${n} missing photo${n === 1 ? "" : "s"}`,
    send: "Send photos",
    sendBusy: (n: number) => `Sending ${n} more photo${n === 1 ? "" : "s"}…`,
    sendNone: "Continue without photos",
    extras: "More photos",
    retake: "Retake",
    remove: "Remove",
    close: "Close",
    lostOnReload: "Some photos didn't finish sending before the page reloaded. Please take them again.",
    offlineSend: "You're offline. We'll send your photos the moment you're back, so keep going.",
    sendFailed: "Couldn't send your photos. Try again.",
  },
} as const
